import { gql } from "@apollo/client";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HTMLCode from "components/common/HTMLCode";
import If from "components/common/If";
import Translate from "components/common/Translate";
import Button from "components/common/button/Button";
import MultiSelectInput, { MultiSelectInputPropsType } from "components/common/form/inputs/MultiSelectInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import DateView from "components/common/views/DateView";
import MoneyView from "components/common/views/MoneyView";
import { getFormContext } from "config/FormContext";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import React from "react";
import { useContext, useRef, useState } from "react";
import { IdName } from "types/common-types";
import { DropdownInputPropsType } from "types/form-types";
import { AccommodationInvestmentMaintenanceFormQuery, AccommodationInvestmentMaintenanceFormQueryVariables, AccommodationMaintenanceTypeDatalistDocument, AccommodationMaintenanceTypeDatalistQuery, AccommodationMaintenanceTypeDatalistQueryVariables, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput, RoomFilterInput, RoomMaintenanceFormQuery, RoomMaintenanceFormQueryVariables, RoomSortInput, SortEnumType } from "types/graphql";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";

const investmentQuery = gql`
query AccommodationInvestmentMaintenanceForm($where: AccommodationWorkInvestmentFilterInput, $sort: [AccommodationWorkInvestmentSortInput!], $skip: Int, $take: Int) {
    accommodationWorkInvestments {
      items(where: $where, skip: $skip, take: $take, order: $sort) {
        id
        name
        total
        startDate
      }
    }
  }
`;

const roomQuery = gql`
query RoomMaintenanceForm($where: RoomFilterInput, $order: [RoomSortInput!], $skip: Int, $take: Int) {
    rooms {
      items(where: $where, skip: $skip, take: $take, order: $order) {
        id
        roomType {
          id,
          name,
          color
        },
        name
      }
    }
  }
`;

type InvestmentModel = {
    id: number,
    name: string,
    total?: number,
    date?: Date
}

type Model = {
    id: string | number,
    investment?: InvestmentModel,
    maintenanceType?: IdName,
    rooms: {
        id: string | number,
        roomId: number,
        roomName: string,
    }[]
}

export type AccommodationWorkMaintenanceLogFormFieldsPropsType = {
    value?: Model[],
    onChange: (value: Model[]) => void,
    name: string,
    required?: boolean,
    min?: number,
    max?: number
}

const AccommodationWorkMaintenanceLogFormFields = (props: AccommodationWorkMaintenanceLogFormFieldsPropsType) => {
    const context = useAppContext();
    const [logError, setLogError] = useState<string>('');
	const formContext = getFormContext(useContext);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    const logTypeDropdownConfig: DropdownInputPropsType<IdName, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput> = {
        getItemId: i => i.id,
        required: true,
        view: {
            inputView: i => i?.name
        },
        dataSource: {
            request: async (params) => {
                let variables: AccommodationMaintenanceTypeDatalistQueryVariables = {};
                if(params.searchText) {
                    variables.where = {
                        name: {
                            contains: params.searchText
                        }
                    }
                }

                var request = await graph<AccommodationMaintenanceTypeDatalistQuery, AccommodationMaintenanceTypeDatalistQueryVariables>({
                    query: AccommodationMaintenanceTypeDatalistDocument,
                    variables
                });

                if(request.errors) {
                    context.notify('error', 'request.error');
                    throw request.errors;
                }

                return request.data!.accommodationMaintenanceTypes!.items.map(i => {
                    return {
                        id: i.id,
                        name: i.name
                    };
                });
            }
        },
        label: "fields.type.name",
        customErrors: [{
            message: context.t('form.errors.required'),
            name: 'required',
            validate(value: IdName | undefined | null) {
                return value?.id ? true : false;
            }
        }]
    }

    const investmentDropdownConfig: DropdownInputPropsType<InvestmentModel, AccommodationMaintenanceTypeFilterInput, AccommodationMaintenanceTypeSortInput> = {
        getItemId: i => i.id,
        required: false,
        dataSource: {
            request: async (params) => {
                let variables: AccommodationInvestmentMaintenanceFormQueryVariables = {
                    where: params.filters,
                    skip: params.skip,
                    take: params.take,
                    sort: params.sort || [{
                        startDate: SortEnumType.Desc
                    }]
                }

                if(params.searchText) {
                    variables.where = appendFilters(variables.where, {
                        name: {
                            contains: params.searchText
                        }
                    });
                }

                try {
                    const response = await graph<AccommodationInvestmentMaintenanceFormQuery, AccommodationInvestmentMaintenanceFormQueryVariables>({
                        query: investmentQuery,
                        variables
                    });

                    if(response.errors) {
                        throw response.errors;
                    }

                    return response.data!.accommodationWorkInvestments!.items.map(i => ({
                        id: i.id,
                        name: i.name,
                        total: i.total,
                        date: new Date(i.startDate)
                    }));
                    
                } catch (error: any) {
                    context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            }
        },
        name: "investment",
        label: "fields.investment.name",
        view: {
            inputView: i => i?.name,
            dropdownView(i) {
                return (
                    <div>
                        <div>{i.name}</div>
                        <div className="text-sm">
                            <DateView date={i.date} />
                            <span> - </span>
                            <i><MoneyView value={i.total} /></i>
                        </div>
                    </div>
                );
            },
        }
    }

    const roomMultiSelectConfig: MultiSelectInputPropsType<Model['rooms']['0'], RoomFilterInput, RoomSortInput> = {
        getItemId: i => i.id,
        required: false,
        dataSource: {
            request: async (params) => {
                let variables: RoomMaintenanceFormQueryVariables = {
                    where: params.filters,
                    skip: params.skip,
                    take: params.take,
                    order: params.sort
                }

                if(params.searchText) {
                    variables.where = appendFilters(variables.where, {
                        or: [
                            {
                                name: {
                                    contains: params.searchText
                                }
                            },
                            {
                                roomType: {
                                    name: {
                                        contains: params.searchText
                                    }
                                }
                            }
                        ]
                    });
                }

                if(props.value?.length) {
                    const roomIds: number[] = [];
                    props.value.forEach(i => {
                        i.rooms.forEach(j => {
                            roomIds.push(j.roomId);
                        });
                    });

                    variables.where = appendFilters(variables.where, {
                        id: {
                            nin: roomIds
                        }
                    });
                }

                try {
                    const response = await graph<RoomMaintenanceFormQuery, RoomMaintenanceFormQueryVariables>({
                        query: roomQuery,
                        variables
                    });

                    if(response.errors) {
                        throw response.errors;
                    }

                    return response.data!.rooms!.items.map(i => ({
                        roomId: i.id,
                        roomName: i.name,
                        id: uuid()
                    }));
                    
                } catch (error: any) {
                    context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            }
        },
        label: "fields.room.name",
        view: {
            inputView: i => i?.roomName,
        }
    }

    const checkValidity = () => {
        let error = '';
        if (props.required && !props.value?.length) {
            error = 'required';
        }
        if (props.min && props.value?.length && props.value.length < props.min) {
            error = 'min';
        }
        if (props.max && props.value?.length && props.value.length > props.max) {
            error = 'max';
        }
        hiddenInputRef.current?.setCustomValidity(error);
        setLogError(error);
    }
    
	const onChange = (model?: Model[]) => {
		if (props.onChange) props.onChange(model || []);
		checkValidity();
	};

    const remove = (index: number) => {
    }

    const add = () => {
    }

    const set = (log: Partial<Model>, index: number) => {
		let value = props.value ? [...props.value] : [];
		value[index] = {
			...value[index],
			...log
		};

		onChange(value);
	};


    return (
        <>
            <h2 className={`${logError && formContext.submitted ? 'text-danger' : ''}`}>Logs</h2>
            <If condition={logError && formContext.submitted}>
                <span className="text-danger text-sm">
                    <Translate i18nKey={`form.errors.array.` + logError}/>
                </span>
			</If>
            <hr/>
            <input title="for validation only" ref={hiddenInputRef} className="hidden" />
            {props.value?.map((log, index) => {
                return (
                    <React.Fragment key={`${props.name}-maintenance-log-${log.id}`}>
                        <div className="hover:bg-neutral-100 dark:hover:bg-neutral-900">
                            <DropdownInput
                                {...logTypeDropdownConfig}
                                value={log.maintenanceType}
                                onChange={maintenanceType => set({maintenanceType}, index)}
                            />
                            <DropdownInput
                                {...investmentDropdownConfig}
                                value={log.investment}
                                onChange={investment => set({investment}, index)}
                            />
                            <MultiSelectInput 
                                {...roomMultiSelectConfig}
                                value={log.rooms}
                                onChange={rooms => set({rooms}, index)}
                            />
							<div className="w-full text-right">
                                <Button title={context.t('button.remove')} theme="link" className="text-danger mt-4"
                                    onClick={() => remove(index)}>
                                    <FontAwesomeIcon icon={faTrash}/>
                                </Button>
                            </div>
                        </div>
                    </React.Fragment>
                )
            })}
			<If condition={!props.max || (props.value?.length || 0) >= props.max}>
				<Button title={context.t('button.add')} className="mt-1" onClick={add}>
					<FontAwesomeIcon icon={faPlus}/>
					<HTMLCode/>
					<Translate i18nKey="button.add"/>
				</Button>
			</If>
        </>
    );
};

export default AccommodationWorkMaintenanceLogFormFields;
